import React from "react"
import { Box, Stack, Text } from "@chakra-ui/react"
import pluralize from "pluralize"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import { Form, TextMessage, WhatsAppTemplate } from "sharedTypes"
import TextBubble from "modals/TextMessage/PreviewTextMessage/Phone/TextBubble"
import MessageBubbleContent from "components/MessageBubbleContent"
import useDefaultOptInText from "services/useDefaultOptInText"
import { getTemplateMediaUrl } from "utilities/whatsappTemplates"
import whatsappBg from "assets/images/phone/whatsapp-bg.webp"

type Props = {
  onSubmit: () => void
  onClose: () => void
  channel: TextMessage["channel"]
  guestCount: number
  textMessageName: string
  eventId: string
  optedInRecipientCount?: number
  unoptedRecipientCount?: number
  autoOptInEnabled?: boolean
  defaultOptInTextId?: number | null
  template?: WhatsAppTemplate | null
  whatsappContentVariables?: Record<string, string>
  body?: string
  forms: Form[]
}

const SendConfirmationModal = ({
  onSubmit, onClose, channel, guestCount, textMessageName, eventId,
  optedInRecipientCount, unoptedRecipientCount,
  autoOptInEnabled, defaultOptInTextId,
  template, whatsappContentVariables, body, forms,
}: Props) => {
  const isWhatsApp = channel === "whatsapp"
  const isOptInTemplate = template?.messageType === "opt_in"
  const recipientLabel = pluralize("recipient", guestCount)

  const hasUnopted = isWhatsApp
    && !isOptInTemplate
    && optedInRecipientCount !== undefined
    && unoptedRecipientCount !== undefined
    && unoptedRecipientCount > 0
  const wantsReviewMode = hasUnopted
    && Boolean(autoOptInEnabled)
    && Boolean(defaultOptInTextId)
  // Opt-in template sends only reach unopted recipients (already-opted are
  // silently dropped by the BE gate). The default modal copy overstates the
  // audience for this case, so render a dedicated message.
  const isOptInTemplateSend = isWhatsApp
    && isOptInTemplate
    && optedInRecipientCount !== undefined
    && unoptedRecipientCount !== undefined

  const { optInText, optInTemplate } = useDefaultOptInText(
    eventId, defaultOptInTextId, wantsReviewMode,
  )

  const reviewMode = wantsReviewMode && Boolean(optInText)

  const title = reviewMode
    ? "Review WhatsApp Delivery"
    : (isWhatsApp ? "Send WhatsApp Message?" : "Send SMS Message?")

  return (
    <ModalForm
      title={title}
      submitText="Send Now"
      size="lg"
      onSubmit={onSubmit}
      onClose={onClose}
    >
      {reviewMode && optInText ? (
        <Stack spacing={5}>
          <Text>
            <Text as="span" fontWeight="medium">
              {`${optedInRecipientCount} ${pluralize("opted-in guest", optedInRecipientCount)}`}
            </Text>
            {" will receive the "}
            <Text as="span" fontWeight="medium">{`"${textMessageName}"`}</Text>
            {" message."}
          </Text>
          <PreviewBubble
            channel={channel}
            template={template}
            whatsappContentVariables={whatsappContentVariables}
            body={body}
            forms={forms}
          />
          <Text>
            <Text as="span" fontWeight="medium">
              {`${unoptedRecipientCount} ${pluralize("guest", unoptedRecipientCount)} who ${unoptedRecipientCount === 1 ? "has" : "have"} not opted in`}
            </Text>
            {" will receive the "}
            <Text as="span" fontWeight="medium">{`"${optInText.name}"`}</Text>
            {" message first. If approved within 48 hours, the "}
            <Text as="span" fontWeight="medium">{`"${textMessageName}"`}</Text>
            {" message will be sent automatically."}
          </Text>
          <PreviewBubble
            channel="whatsapp"
            template={optInTemplate}
            whatsappContentVariables={optInText.whatsappContentVariables}
            body={optInText.body}
            forms={forms}
          />
        </Stack>
      ) : (
        <>
          {isOptInTemplateSend ? (
            <Text>
              The opt-in message will be sent to {unoptedRecipientCount}
              {" "}{pluralize("recipient", unoptedRecipientCount)} who
              {" "}{unoptedRecipientCount === 1 ? "hasn't" : "haven't"} opted in yet.
              {optedInRecipientCount! > 0 && (
                <>
                  {" "}The remaining {optedInRecipientCount}
                  {" "}{pluralize("guest", optedInRecipientCount)}
                  {" "}{optedInRecipientCount === 1 ? "is" : "are"} already opted in and won&apos;t receive it.
                </>
              )}
            </Text>
          ) : hasUnopted ? (
            <Text>
              You&apos;re sending to {guestCount} {recipientLabel}.
              {" "}{optedInRecipientCount} will receive this message right away.
              {" "}The remaining {unoptedRecipientCount} {" "}
              haven&apos;t opted in yet and will not receive this message.
            </Text>
          ) : (
            <Text>You are about to send your text message to {guestCount} {recipientLabel}.</Text>
          )}
          <Box mt={4}>
            <PreviewBubble
              channel={channel}
              template={template}
              whatsappContentVariables={whatsappContentVariables}
              body={body}
              forms={forms}
            />
          </Box>
        </>
      )}
    </ModalForm>
  )
}

type PreviewBubbleProps = {
  channel: TextMessage["channel"]
  template?: WhatsAppTemplate | null
  whatsappContentVariables?: Record<string, string>
  body?: string
  forms: Form[]
}

const PreviewBubble = ({
  channel, template, whatsappContentVariables, body, forms,
}: PreviewBubbleProps) => (
  <Box
    borderRadius="md"
    overflow="hidden"
    {...channel === "whatsapp" ? {
      bgImage: `url(${whatsappBg})`,
      bgSize: "cover",
      bgPos: "center",
      borderWidth: 1,
      borderColor: "gray.200",
    } : {}}
    p={4}
  >
    <TextBubble
      text={<MessageBubbleContent body={body} forms={forms} />}
      mediaUrl={template
        ? getTemplateMediaUrl(template, whatsappContentVariables)
        : undefined}
      buttons={template?.buttons}
      channel={channel}
    />
  </Box>
)

export default SendConfirmationModal
